/**
 * WHO-6309. Invoice on behalf of the seller, VAT per item, ex-VAT price and
 * the seller invoice upload. Contract: specs/WHO-6309-seller-invoice-contract.md.
 */

/** Statsig gate for every WHO-6309 surface. Off (or loading) renders nothing new. */
export const SELLER_INVOICE_UI_GATE = "seller_invoice_ui";

/** The mandate text version the seller accepts (stored by the gateway with a timestamp). */
export const INVOICE_MANDATE_VERSION = "2026-10";

/** D9: the upload accepts one PDF up to 5 MB. */
export const SELLER_INVOICE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
export const SELLER_INVOICE_UPLOAD_MIME = "application/pdf";

/** `VatRegime` from the contract (D6). Hand-typed until the gateway field is in codegen. */
export const VAT_REGIME = {
  margin: "MARGIN",
  standard: "STANDARD",
  exemptKor: "EXEMPT_KOR",
} as const;

export type VatRegime = (typeof VAT_REGIME)[keyof typeof VAT_REGIME];

/** `sellerInvoiceMode` from the contract. */
export const SELLER_INVOICE_MODE = {
  generated: "GENERATED",
  upload: "UPLOAD",
  none: "NONE",
} as const;

export type SellerInvoiceMode =
  (typeof SELLER_INVOICE_MODE)[keyof typeof SELLER_INVOICE_MODE];

export const SELLER_INVOICE_LOCALES = [
  "nl",
  "en",
  "de",
  "fr",
  "it",
  "es",
] as const;

export type SellerInvoiceLocale = (typeof SELLER_INVOICE_LOCALES)[number];

type LocalisedCopy = Record<SellerInvoiceLocale, string>;

/**
 * Every new string, keyed by its Lokalise key, with the six locales as the
 * interim fallback until the keys are seeded in Lokalise.
 */
export const SELLER_INVOICE_COPY = {
  "common:seller-invoice-price-excl-vat": {
    nl: "{{price}} excl. btw",
    en: "{{price}} excl. VAT",
    de: "{{price}} zzgl. MwSt.",
    fr: "{{price}} HT",
    it: "{{price}} IVA esclusa",
    es: "{{price}} sin IVA",
  },
  "common:seller-invoice-margin-not-deductible": {
    nl: "Btw niet aftrekbaar (margeregeling)",
    en: "VAT not deductible (margin scheme)",
    de: "MwSt. nicht abziehbar (Differenzbesteuerung)",
    fr: "TVA non déductible (régime de la marge)",
    it: "IVA non detraibile (regime del margine)",
    es: "IVA no deducible (régimen de margen)",
  },
  "account-payment:purchases-order-summary": {
    nl: "Aankoopoverzicht",
    en: "Order summary",
    de: "Kaufübersicht",
    fr: "Récapitulatif d'achat",
    it: "Riepilogo acquisto",
    es: "Resumen de compra",
  },
  "account-payment:purchases-seller-invoice": {
    nl: "Factuur van de verkoper",
    en: "Seller's invoice",
    de: "Rechnung des Verkäufers",
    fr: "Facture du vendeur",
    it: "Fattura del venditore",
    es: "Factura del vendedor",
  },
  "account-payment:purchases-seller-invoice-uploaded": {
    nl: "Geüpload door de verkoper",
    en: "Uploaded by the seller",
    de: "Vom Verkäufer hochgeladen",
    fr: "Téléversée par le vendeur",
    it: "Caricata dal venditore",
    es: "Subida por el vendedor",
  },
  "account-payment:purchases-seller-invoice-pending": {
    nl: "Volgt",
    en: "To follow",
    de: "Folgt",
    fr: "À venir",
    it: "In arrivo",
    es: "Próximamente",
  },
  "account-payment:purchases-whoppah-invoice": {
    nl: "Whoppah-factuur",
    en: "Whoppah invoice",
    de: "Whoppah-Rechnung",
    fr: "Facture Whoppah",
    it: "Fattura Whoppah",
    es: "Factura de Whoppah",
  },
  "account-payment:purchases-whoppah-invoice-hint": {
    nl: "Verzending en kopersbescherming",
    en: "Shipping and buyer protection",
    de: "Versand und Käuferschutz",
    fr: "Livraison et protection acheteur",
    it: "Spedizione e protezione acquirente",
    es: "Envío y protección al comprador",
  },
  "account-payment:sales-seller-invoice": {
    nl: "Factuur aan de koper",
    en: "Invoice to the buyer",
    de: "Rechnung an den Käufer",
    fr: "Facture à l'acheteur",
    it: "Fattura all'acquirente",
    es: "Factura al comprador",
  },
  "account-payment:sales-sales-confirmation": {
    nl: "Verkoopbevestiging",
    en: "Sales confirmation",
    de: "Verkaufsbestätigung",
    fr: "Confirmation de vente",
    it: "Conferma di vendita",
    es: "Confirmación de venta",
  },
  "account-payment:sales-commission-invoice": {
    nl: "Factuur commissie",
    en: "Commission invoice",
    de: "Provisionsrechnung",
    fr: "Facture de commission",
    it: "Fattura commissione",
    es: "Factura de comisión",
  },
  "account-payment:sales-commission-invoice-hint": {
    nl: "Van Whoppah, met btw-specificatie",
    en: "From Whoppah, with the VAT breakdown",
    de: "Von Whoppah, mit MwSt.-Aufstellung",
    fr: "De Whoppah, avec le détail de la TVA",
    it: "Da Whoppah, con il dettaglio IVA",
    es: "De Whoppah, con el desglose del IVA",
  },
  "account-payment:purchase-whoppah-invoice-file": {
    nl: "Factuur",
    en: "Invoice",
    de: "Rechnung",
    fr: "Facture",
    it: "Fattura",
    es: "Factura",
  },
  "account-payment:sales-seller-invoice-upload": {
    nl: "Factuur uploaden (PDF, max. 5 MB)",
    en: "Upload invoice (PDF, max. 5 MB)",
    de: "Rechnung hochladen (PDF, max. 5 MB)",
    fr: "Téléversez votre facture (PDF, 5 Mo max.)",
    it: "Carica la fattura (PDF, max. 5 MB)",
    es: "Subir factura (PDF, máx. 5 MB)",
  },
  "account-payment:sales-seller-invoice-replace": {
    nl: "Factuur vervangen",
    en: "Replace invoice",
    de: "Rechnung ersetzen",
    fr: "Remplacez la facture",
    it: "Sostituisci la fattura",
    es: "Sustituir factura",
  },
  "account-payment:sales-seller-invoice-upload-hint": {
    nl: "De koper ziet je factuur naast het aankoopoverzicht.",
    en: "The buyer sees your invoice next to the order summary.",
    de: "Der Käufer sieht deine Rechnung neben der Kaufübersicht.",
    fr: "L'acheteur voit votre facture à côté du récapitulatif d'achat.",
    it: "L'acquirente vede la tua fattura accanto al riepilogo acquisto.",
    es: "El comprador ve tu factura junto al resumen de compra.",
  },
  "account-payment:sales-seller-invoice-upload-done": {
    nl: "Factuur geüpload",
    en: "Invoice uploaded",
    de: "Rechnung hochgeladen",
    fr: "Facture téléversée",
    it: "Fattura caricata",
    es: "Factura subida",
  },
  "account-payment:sales-seller-invoice-not-pdf": {
    nl: "Kies een PDF-bestand.",
    en: "Choose a PDF file.",
    de: "Wähle eine PDF-Datei.",
    fr: "Choisissez un fichier PDF.",
    it: "Scegli un file PDF.",
    es: "Elige un archivo PDF.",
  },
  "account-payment:sales-seller-invoice-too-large": {
    nl: "Het bestand is groter dan 5 MB.",
    en: "The file is larger than 5 MB.",
    de: "Die Datei ist größer als 5 MB.",
    fr: "Le fichier dépasse 5 Mo.",
    it: "Il file supera i 5 MB.",
    es: "El archivo supera los 5 MB.",
  },
  "account-payment:sales-seller-invoice-upload-error": {
    nl: "Uploaden is niet gelukt. Probeer het opnieuw.",
    en: "The upload did not work. Please try again.",
    de: "Das Hochladen hat nicht geklappt. Versuch es erneut.",
    fr: "Le téléversement a échoué. Veuillez réessayer.",
    it: "Caricamento non riuscito. Riprova.",
    es: "No se pudo subir. Inténtalo de nuevo.",
  },
  "account:seller-invoice-mandate-title": {
    nl: "Facturatie namens jou",
    en: "Invoicing on your behalf",
    de: "Rechnungsstellung in deinem Namen",
    fr: "Facturation en votre nom",
    it: "Fatturazione per tuo conto",
    es: "Facturación en tu nombre",
  },
  "account:seller-invoice-mandate-label": {
    nl: "Ik geef Whoppah toestemming om namens mij facturen aan kopers uit te reiken, zoals beschreven in de algemene voorwaarden.",
    en: "I allow Whoppah to issue invoices to buyers on my behalf, as described in the terms and conditions.",
    de: "Ich erlaube Whoppah, in meinem Namen Rechnungen an Käufer auszustellen, wie in den AGB beschrieben.",
    fr: "J'autorise Whoppah à émettre des factures aux acheteurs en mon nom, comme décrit dans les conditions générales.",
    it: "Autorizzo Whoppah a emettere fatture agli acquirenti per mio conto, come descritto nei termini e condizioni.",
    es: "Autorizo a Whoppah a emitir facturas a los compradores en mi nombre, tal como se describe en los términos y condiciones.",
  },
  "account:seller-invoice-mandate-terms-link": {
    nl: "Algemene voorwaarden",
    en: "Terms and conditions",
    de: "AGB",
    fr: "Conditions générales",
    it: "Termini e condizioni",
    es: "Términos y condiciones",
  },
  "account:seller-invoice-mandate-accepted": {
    nl: "Geaccepteerd op {{date}}",
    en: "Accepted on {{date}}",
    de: "Akzeptiert am {{date}}",
    fr: "Accepté le {{date}}",
    it: "Accettato il {{date}}",
    es: "Aceptado el {{date}}",
  },
  "account:seller-invoice-mandate-error": {
    nl: "Opslaan is niet gelukt. Probeer het opnieuw.",
    en: "Saving did not work. Please try again.",
    de: "Speichern hat nicht geklappt. Versuch es erneut.",
    fr: "L'enregistrement a échoué. Veuillez réessayer.",
    it: "Salvataggio non riuscito. Riprova.",
    es: "No se pudo guardar. Inténtalo de nuevo.",
  },
  "account:seller-invoice-vat-regime": {
    nl: "Btw-regeling",
    en: "VAT scheme",
    de: "MwSt.-Regelung",
    fr: "Régime de TVA",
    it: "Regime IVA",
    es: "Régimen de IVA",
  },
  "account:seller-invoice-vat-regime-hint": {
    nl: "Ingesteld door Whoppah. Klopt dit niet? Neem contact op met support.",
    en: "Set by Whoppah. Not right? Contact support.",
    de: "Von Whoppah festgelegt. Stimmt das nicht? Kontaktiere den Support.",
    fr: "Défini par Whoppah. Ce n'est pas correct ? Contactez le support.",
    it: "Impostato da Whoppah. Non è corretto? Contatta il supporto.",
    es: "Configurado por Whoppah. ¿No es correcto? Contacta con soporte.",
  },
  "account:seller-invoice-vat-regime-margin": {
    nl: "Margeregeling",
    en: "Margin scheme",
    de: "Differenzbesteuerung",
    fr: "Régime de la marge",
    it: "Regime del margine",
    es: "Régimen de margen",
  },
  "account:seller-invoice-vat-regime-standard": {
    nl: "Standaard btw",
    en: "Standard VAT",
    de: "Regelbesteuerung",
    fr: "TVA standard",
    it: "IVA ordinaria",
    es: "IVA estándar",
  },
  "account:seller-invoice-vat-regime-exempt-kor": {
    nl: "Vrijgesteld (kleineondernemersregeling)",
    en: "Exempt (small business scheme)",
    de: "Befreit (Kleinunternehmerregelung)",
    fr: "Exonéré (franchise en base)",
    it: "Esente (regime forfettario)",
    es: "Exento (régimen de pequeñas empresas)",
  },
} satisfies Record<string, LocalisedCopy>;

export type SellerInvoiceCopyKey = keyof typeof SELLER_INVOICE_COPY;

export const VAT_REGIME_COPY_KEY: Record<VatRegime, SellerInvoiceCopyKey> = {
  [VAT_REGIME.margin]: "account:seller-invoice-vat-regime-margin",
  [VAT_REGIME.standard]: "account:seller-invoice-vat-regime-standard",
  [VAT_REGIME.exemptKor]: "account:seller-invoice-vat-regime-exempt-kor",
};
